/**
 * The "Neuigkeiten" between server and screen.
 *
 * **The ids come from the server, the words are compiled into the bundle** —
 * and an app or a browser tab older than the API puts the two out of step.
 * Reiselust saw it in production: the cards read the raw key four times on the
 * one screen whose job is to explain what changed. So the client keeps only
 * entries it has words for; the rest stay unacknowledged (only what was shown
 * is acknowledged) and arrive in full with the next update.
 */
import { ANNOUNCEMENTS, type Announcement } from '@fsv/core';
import { resolveHelpText } from '../help/facts';
import { NEWS_TEXTS } from './texts';

export interface AnnouncementRef {
  readonly id: string;
  readonly date: string;
}

/** A card: the entry and its words, resolved. */
export interface NewsItem extends AnnouncementRef {
  readonly title: string;
  readonly body: string;
}

/** Whether this build can say anything about `id`. */
export function hasNewsText(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(NEWS_TEXTS, id);
}

/**
 * What the server named, reduced to what this build can render, in the
 * server's order (oldest first). Unknown ids are dropped, never shown as keys.
 */
export function announcementsWithWords(refs: readonly AnnouncementRef[]): NewsItem[] {
  return refs.filter((r) => hasNewsText(r.id)).map((r) => newsItem(r));
}

function newsItem(ref: AnnouncementRef): NewsItem {
  const text = NEWS_TEXTS[ref.id] as (typeof NEWS_TEXTS)[string];
  return { id: ref.id, date: ref.date, title: resolveHelpText(text.title), body: resolveHelpText(text.body) };
}

/** Every announcement of this build, newest first — the list in the help. */
export function allNews(): NewsItem[] {
  return [...ANNOUNCEMENTS].reverse().filter((a: Announcement) => hasNewsText(a.id)).map(newsItem);
}

/**
 * Whether the cards may show at all: a real session on the API always; the
 * demo only on the demo backend and once its tour is over (the tour comes
 * first). Never a mix of the two.
 */
export function newsMayShow(backendKind: 'api' | 'demo', sessionMode: 'api' | 'demo', tourOver: boolean): boolean {
  if (backendKind !== sessionMode) return false;
  return backendKind === 'api' ? true : tourOver;
}

/**
 * What the demo shows as "Neu": the newest few, so marketing can show the
 * cards without a server. Fixed, not a history: the demo has no account age.
 */
export const DEMO_NEWS_COUNT = 2;
export function demoAnnouncements(acknowledged: readonly string[]): AnnouncementRef[] {
  const seen = new Set(acknowledged);
  return ANNOUNCEMENTS.slice(-DEMO_NEWS_COUNT)
    .filter((a) => !seen.has(a.id))
    .map((a) => ({ id: a.id, date: a.date }));
}
